/**
 * L1J Web Client - QuantityDialog Component
 * 개수 입력 창 (겹치는 아이템을 나눠 버리거나 나눠 주울 때)
 *
 * 서버 `C_DropItem` / `C_PickUpItem`은 objId와 함께 **개수**를 읽는다. 물약 7개가
 * 한 슬롯에 겹쳐 있을 때 몇 개를 내려놓을지는 클라이언트만 알 수 있으므로, 겹친
 * 아이템이면 이 창으로 묻는다 (원본 클라이언트도 같은 자리에서 개수를 묻는다).
 *
 * 창은 상태를 갖지 않는다 — 어떤 아이템을 어디에 놓으려던 중인지는 GameScene이
 * 들고 있고, 이 창은 "숫자 하나"만 받아 콜백으로 돌려준다.
 */

import Phaser from 'phaser';

export interface QuantityDialogOptions {
  /** 창 제목 (예: "몇 개를 버릴까요?"). */
  title: string;
  /** 대상 아이템 표시명. 무엇을 나누는 중인지 보여준다. */
  itemName: string;
  /** 최댓값 = 가진 개수. 입력은 1..max로 잘린다. */
  max: number;
  /** 확인 버튼 문구 (기본 '확인'). */
  confirmLabel?: string;
}

const PANEL_WIDTH = 300;
const PANEL_HEIGHT = 168;

const ACCENT_COLOR = 0xffd700;
const ACCENT_TEXT_COLOR = '#ffd700';

/** 창 depth. TargetPrompt(300)보다 위 — 개수를 묻는 동안은 이 창이 가장 앞이다. */
const DIALOG_DEPTH = 320;

export class QuantityDialog extends Phaser.GameObjects.Container {
  private panelBg!: Phaser.GameObjects.Rectangle;
  private titleText!: Phaser.GameObjects.Text;
  private itemText!: Phaser.GameObjects.Text;
  private hintText!: Phaser.GameObjects.Text;
  private confirmText!: Phaser.GameObjects.Text;

  /** 개수 입력용 DOM 요소. ChatBox/TargetPrompt와 같은 이유로 씬 최상위에 붙인다. */
  private countInput!: HTMLInputElement;
  private inputContainer!: Phaser.GameObjects.DOMElement;

  private max: number = 1;
  private onConfirm: ((count: number) => void) | null = null;
  private onCancel: (() => void) | null = null;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this);

    this.setScrollFactor(0);
    this.createUI();
    this.createInput(x, y);

    this.setVisible(false);
    this.setDepth(DIALOG_DEPTH);
  }

  private createUI(): void {
    this.panelBg = this.scene.add.rectangle(0, 0, PANEL_WIDTH, PANEL_HEIGHT, 0x1a1a2e, 0.97);
    this.panelBg.setStrokeStyle(2, ACCENT_COLOR);
    this.panelBg.setScrollFactor(0);
    // 창 뒤쪽(월드/인벤토리)으로 클릭이 새지 않게 막는다.
    this.panelBg.setInteractive();
    this.add(this.panelBg);

    this.titleText = this.scene.add.text(0, -PANEL_HEIGHT / 2 + 16, '', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: ACCENT_TEXT_COLOR,
    });
    this.titleText.setOrigin(0.5, 0);
    this.titleText.setScrollFactor(0);
    this.add(this.titleText);

    this.itemText = this.scene.add.text(0, -PANEL_HEIGHT / 2 + 40, '', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#dddddd',
      align: 'center',
      wordWrap: { width: PANEL_WIDTH - 30 },
    });
    this.itemText.setOrigin(0.5, 0);
    this.itemText.setScrollFactor(0);
    this.add(this.itemText);

    this.hintText = this.scene.add.text(0, PANEL_HEIGHT / 2 - 46, '', {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      color: '#999999',
    });
    this.hintText.setOrigin(0.5, 0);
    this.hintText.setScrollFactor(0);
    this.add(this.hintText);

    // 확인 / 취소 버튼
    this.confirmText = this.createButton(-62, PANEL_HEIGHT / 2 - 24, '확인', ACCENT_TEXT_COLOR, () =>
      this.submit()
    );
    this.createButton(62, PANEL_HEIGHT / 2 - 24, '취소', '#cccccc', () => this.cancel());
  }

  private createButton(
    x: number,
    y: number,
    label: string,
    color: string,
    onClick: () => void
  ): Phaser.GameObjects.Text {
    const bg = this.scene.add.rectangle(x, y, 96, 26, 0x2a2a44, 1);
    bg.setStrokeStyle(1, 0x6a6aaa);
    bg.setScrollFactor(0);
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(0x3a3a5c, 1));
    bg.on('pointerout', () => bg.setFillStyle(0x2a2a44, 1));
    bg.on('pointerdown', onClick);
    this.add(bg);

    const text = this.scene.add.text(x, y, label, {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color,
    });
    text.setOrigin(0.5);
    text.setScrollFactor(0);
    this.add(text);
    return text;
  }

  /**
   * 개수 입력창 생성.
   *
   * Phaser 키보드 매니저는 window에 리스너를 걸기 때문에, 입력 중 눌린 키가 씬의
   * 단축키(I/E/C/M/ESC)로도 해석된다. ChatBox/TargetPrompt와 동일하게 keydown에서
   * stopPropagation()으로 막는다.
   */
  private createInput(x: number, y: number): void {
    this.countInput = document.createElement('input');
    this.countInput.type = 'number';
    this.countInput.min = '1';
    this.countInput.style.cssText = `
      width: ${PANEL_WIDTH - 80}px;
      padding: 6px 10px;
      font-size: 14px;
      font-family: Arial, sans-serif;
      text-align: center;
      background: #0a0a15;
      border: 1px solid #6a6aaa;
      border-radius: 4px;
      color: #ffffff;
      outline: none;
    `;

    this.countInput.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        this.submit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.cancel();
      }
    });

    // 위치는 컨테이너 로컬이 아니라 씬 절대 좌표다 (ChatBox/TargetPrompt와 같은 규약).
    this.inputContainer = this.scene.add.dom(x, y - 6, this.countInput);
    this.inputContainer.setScrollFactor(0);
    this.inputContainer.setVisible(false);
    this.inputContainer.setDepth(DIALOG_DEPTH);
  }

  public isOpen(): boolean {
    return this.visible;
  }

  /** 창 크기. 씬이 "이 지점이 창 위인가"를 판정할 때 쓴다. */
  public getPanelWidth(): number {
    return PANEL_WIDTH;
  }

  public getPanelHeight(): number {
    return PANEL_HEIGHT;
  }

  /**
   * 개수를 묻는다. 이미 열려 있으면 새 요청으로 갈아 끼운다 (이전 요청은 취소
   * 콜백 없이 버린다 — 아이템을 두 번 처리하지 않기 위해서다).
   */
  public show(
    options: QuantityDialogOptions,
    onConfirm: (count: number) => void,
    onCancel?: () => void
  ): void {
    this.max = Math.max(1, Math.floor(options.max));
    this.onConfirm = onConfirm;
    this.onCancel = onCancel ?? null;

    this.titleText.setText(options.title);
    this.itemText.setText(options.itemName);
    this.hintText.setText(`1 ~ ${this.max}  (Enter 확인 / ESC 취소)`);
    this.confirmText.setText(options.confirmLabel ?? '확인');

    this.countInput.max = String(this.max);
    this.countInput.value = String(this.max);

    this.setVisible(true);
    this.inputContainer.setVisible(true);

    // 포커스는 다음 프레임에 준다 — 창을 연 클릭이 아직 처리 중이면 곧바로 풀린다.
    this.scene.time.delayedCall(0, () => {
      if (!this.visible) return;
      this.countInput.focus();
      this.countInput.select();
    });
  }

  public hide(): void {
    this.setVisible(false);
    this.inputContainer.setVisible(false);
    this.countInput.blur();
    this.onConfirm = null;
    this.onCancel = null;
  }

  /**
   * 입력값을 1..max로 자른 뒤 확인 콜백에 넘긴다.
   * 숫자가 아니면 아무 일도 하지 않는다 (창은 열어 둔 채 다시 입력받는다).
   */
  private submit(): void {
    const parsed = Number.parseInt(this.countInput.value, 10);
    if (!Number.isFinite(parsed)) return;

    const count = Math.min(this.max, Math.max(1, parsed));
    const callback = this.onConfirm;
    this.hide();
    callback?.(count);
  }

  private cancel(): void {
    const callback = this.onCancel;
    this.hide();
    callback?.();
  }

  public destroy(fromScene?: boolean): void {
    this.inputContainer?.destroy();
    super.destroy(fromScene);
  }
}
